import React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faInfoCircle, faTimes } from '@fortawesome/free-solid-svg-icons';
import './TradeRecapBackModal.css';

/**
 * Deal Confirmation Modal
 * 
 * Reusable confirmation modal using TradeRecapBackModal styling
 * for draft and recap actions in the Offer Generator
 * 
 * @param {Object} props
 * @param {string} props.title - Modal title
 * @param {string} props.message - Confirmation message to display
 * @param {string} props.confirmText - Text for confirm button
 * @param {string} props.cancelText - Text for cancel button
 * @param {Function} props.onConfirm - Callback when user confirms
 * @param {Function} props.onCancel - Callback when user cancels
 * @param {string} props.iconType - 'info' or 'success' (default: 'info')
 * @param {React.ReactNode} props.children - extra content under the message
 *   (the recap pop-up's quantity per product, for a deal with several products)
 * @param {boolean} props.confirmDisabled - the confirm cannot go ahead yet
 */
const DealConfirmationModal = ({ 
  title = 'Confirm Action',
  message, 
  confirmText = 'Confirm',
  cancelText = 'Cancel',
  onConfirm, 
  onCancel,
  iconType = 'info',
  children = null,
  confirmDisabled = false,
}) => {
  // Prevent modal from closing when clicking inside it
  const handleModalClick = (e) => {
    e.stopPropagation();
  };

  // Close modal when clicking overlay
  const handleOverlayClick = () => {
    onCancel();
  };

  return (
    <div className="trade-recap-back-modal-overlay" onClick={handleOverlayClick}>
      <div className="trade-recap-back-modal" onClick={handleModalClick}>
        {/* Header with HMS Blue Gradient */}
        <div className="trade-recap-back-modal-header">
          <div className="trade-recap-back-header-content">
            <h3 className="trade-recap-back-modal-title">{title}</h3>
          </div>
          <button
            className="trade-recap-back-close-btn"
            onClick={onCancel}
            aria-label="Close modal"
          >
            <FontAwesomeIcon icon={faTimes} />
          </button>
        </div>

        {/* Body Content */}
        <div className="trade-recap-back-modal-body">
          {/* Info Banner */}
          <div className="trade-recap-back-warning-banner" style={{
            background: iconType === 'success' 
              ? 'linear-gradient(135deg, #f0fdf4, #dcfce7)' 
              : 'linear-gradient(135deg, #eff6ff, #dbeafe)',
            borderLeft: iconType === 'success' 
              ? '4px solid #10B981' 
              : '4px solid #4a6b25'
          }}>
            <FontAwesomeIcon 
              icon={faInfoCircle} 
              size="lg"
              style={{
                color: iconType === 'success' ? '#10B981' : '#4a6b25'
              }}
            />
            <div className="trade-recap-back-warning-text">
              <strong style={{
                color: iconType === 'success' ? '#065f46' : '#1e3a8a'
              }}>Information</strong>
              <p style={{
                color: iconType === 'success' ? '#047857' : '#1e40af'
              }}>
                {message}
              </p>
            </div>
          </div>
          {children}
        </div>

        {/* Footer with Action Buttons */}
        <div className="trade-recap-back-modal-footer">
          <button
            className="trade-recap-back-btn-secondary"
            onClick={onCancel}
          >
            {cancelText}
          </button>
          <button
            className="trade-recap-back-btn-primary"
            onClick={onConfirm}
            disabled={confirmDisabled}
          >
            {confirmText}
          </button>
        </div>
      </div>
    </div>
  );
};

export default DealConfirmationModal;
